import { faRectangleVerticalHistory } from "@fortawesome/pro-regular-svg-icons/faRectangleVerticalHistory";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CardContent, IconButton, useTheme } from '@mui/material';
import { MprcCard, MprcContainer, MprcInfiniteLoading, MprcRequestErrorReport } from 'components';
import { FeedItemDto, FeedType } from 'generated/CoreApi_v1';
import { useWindowSize } from "hooks/useWindowSize";
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { useGobackOrKnowledge } from "views/common/hooks/useGobackOrHome";
import { CardManager } from 'views/knowledge/components/card/CardManager';
import { CommentBox } from 'views/knowledge/components/comment/CommentBox';
import { PublicationContentBox } from "views/knowledge/publication/pages/components/PublicationContentBox";
import { RightPanelContentPage } from "../common/RightPanelContentPage";
import { PublicationHeader } from '../components/PublicationHeader';

////////////////////////////////////////////////////////////////////////////////////////////////

export const PollThreadViewer: React.FC = () => {

    const { id: publicationId } = useParams();
    const location = useLocation();
    const gobackOrHome = useGobackOrKnowledge();

    const api = useCoreApi();
    const theme = useTheme();
    const { width } = useWindowSize({ wait: 50 });

    const [loading, setLoading] = useState<boolean>(true);
    const [publication, setPublication] = useState<FeedItemDto>();
    const [openRightPanel, setOpenRightPanel] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();

    ///
    const load = async (publicationId: string) => {
        let markRead = true;

        // Se a solicitação foi realizada a partir do Feed de Histórico,
        // não deve marcar a Publicação como 'Lida', para não alterar o histórico de visualização.
        if (location.state?.hasOwnProperty("previousPathname")) {
            const previousPathname = location.state.previousPathname as string;
            markRead = !previousPathname.startsWith(KnowledgePaths.FeedHistory);
        }

        setLoading(true);
        try {
            const result = await api.v1.knowledgeFeed.getFeedItem({ publicationId, markRead });
            const record = result.data;

            setPublication(record);
        }
        catch (error: any) {
            setViewError(error);
        }
        finally {
            setLoading(false);
        }
    };

    ///
    const onDataItemChanged = (data: Partial<FeedItemDto>) => {
        setPublication(prevState => {
            return { ...prevState!, ...data };
        });
    };

    useEffect(() => {
        if (publicationId != null) {
            load(publicationId);
        }
    }, []);

    useEffect(() => {
        if (openRightPanel && width > theme.breakpoints.values["md"])
            setOpenRightPanel(false);
    }, [width]);

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderContentPanel = useCallback(() => {
        return (
            <>
                <MprcContainer>
                    <CardManager
                        feedType={FeedType.ThreadItemContentPage}
                        pub={publication!}
                        onNeedResetRowHeight={() => { }}
                        onReload={() => load(publicationId!)}
                        onFeedItemChanged={(data) => {
                            if (data.deletedOn) gobackOrHome();
                            else onDataItemChanged(data);
                        }}
                    />
                </MprcContainer>

                {
                    publication &&
                    <MprcContainer>
                        <MprcCard>
                            <CardContent>
                                <CommentBox publicationId={publication.publicationId} publicationTitle={publication.title} readOnly={publication.readOnly} />
                            </CardContent>
                        </MprcCard>
                    </MprcContainer>
                }
            </>
        );
    }, [publication]);

    return (
        <div>
            {
                viewError != undefined
                    ? <MprcRequestErrorReport error={viewError} />
                    : loading
                        ? <MprcInfiniteLoading visible />
                        : publication != undefined &&
                        <>
                            <MprcContainer position="sticky" margin="none">
                                <PublicationHeader
                                    onClose={gobackOrHome}
                                    actions={
                                        width < theme.breakpoints.values["md"]
                                            ? [<IconButton onClick={() => setOpenRightPanel(state => !state)}><FontAwesomeIcon icon={faRectangleVerticalHistory} /></IconButton>]
                                            : []
                                    }
                                />
                            </MprcContainer>
                            <PublicationContentBox
                                openRightPanel={openRightPanel}
                                onCloseRighPanel={() => setOpenRightPanel(false)}
                                content={RenderContentPanel()}
                                rightPanel={publication != undefined ? RightPanelContentPage(publication) : undefined}
                                maxWidth="lg"
                            />
                        </>
            }
        </div>
    );
};
